import React, {useEffect, useRef, useState} from "react";
import {useToast} from "../ui/use-toast";
import {CardDescription, CardHeader, CardTitle} from "../ui/card";
import {Separator} from "../ui/separator";
import {Button} from "../ui/button";
import {useAuthInfo} from "@propelauth/react";
import {useParams} from "react-router-dom";
import {Cell, linesDecoder, linesRowMeta, RowMeta, SovConfig} from "./bidConfig_model";
import DataSheet, {SheetEdits} from "./DataSheet";
import {deleteWithToken, fetchWithToken, postWithToken, putWithToken} from "../../api/apiBase";
import {CopyButton2} from "../exportData/CopyButton";
import {copySheetDataToClipboard} from "./datasheetHelpers";


/**
 * the SOV editor - the scope's SOV (the project's on the project setup tab).
 * every change is saved on its own (server bid_sov_routes) - there is no save button.
 * changes go one at a time, in order. after a change to the rows / columns / cell kinds the grid is drawn
 * from what the server sends back (new cells come with their ids)
 * */
export function DevBidFormConfig() {

    const {toast} = useToast();
    const {project_id, scope_id} = useParams<{ project_id: string, scope_id: string }>();
    const {accessToken} = useAuthInfo();
    // null - no SOV yet
    const [config, setConfig] = useState<SovConfig | null>(null);
    const [loaded, setLoaded] = useState(false);
    // the grid - its own state so what is typed shows before it is saved
    const [data, setData] = useState<Cell[][]>([]);
    const [rowMeta, setRowMeta] = useState<RowMeta[]>([]);
    // the changes waiting their turn
    const queue = useRef<Promise<any>>(Promise.resolve());

    const sovUrl = `/projects/${project_id}/sov`;
    const scopeQuery = scope_id ? `?scope_id=${scope_id}` : "";
    const token = accessToken as string;

    // the SOV as the server has it - the grid is drawn from it
    const show = (cfg: SovConfig | null) => {
        setConfig(cfg);
        setData(cfg ? linesDecoder(cfg.lines) : []);
        setRowMeta(cfg ? linesRowMeta(cfg.lines) : []);
    }

    const load = () => {
        if (!project_id || !accessToken) return;
        fetchWithToken(sovUrl + scopeQuery, token)
            .then((cfg: SovConfig | null) => {
                show(cfg);
                setLoaded(true);
            })
            .catch((err) => console.error("Failed to load the SOV", err));
    }

    useEffect(load, [project_id, scope_id]);

    // a change that didn't save - say so, and show the SOV as the server has it
    const failed = (err: any) => {
        console.error(err);
        toast({title: "Not saved", description: "The SOV was reloaded - try again.", variant: "destructive"});
        load();
    }

    // one change, after the ones before it. the grid is redrawn from what comes back -
    // except for text, so what is being typed in the next cell stays
    const change = (request: () => Promise<SovConfig>, redraw: boolean = true) => {
        queue.current = queue.current
            .then(request)
            .then((cfg: SovConfig) => redraw ? show(cfg) : setConfig(cfg))
            .catch(failed);
    }

    const onCreate = () => change(() => postWithToken(sovUrl + scopeQuery, undefined, token));

    const at = config ? `${sovUrl}/${config.id}` : "";
    // the cell ids of the grid as it is now - the server finds lines / columns by a cell in them
    const id = (i: number, j: number) => data[i][j].id as number;

    const edits: SheetEdits = {
        cellText: (i, j, text) =>
            change(() => putWithToken(`${at}/cells/${id(i, j)}`, JSON.stringify({text}), token), false),
        cellInput: (i, j, input) =>
            change(() => putWithToken(`${at}/cells/${id(i, j)}`, JSON.stringify({kind: input ? "input" : "label"}), token)),
        cellType: (i, j, type) =>
            change(() => putWithToken(`${at}/cells/${id(i, j)}`, JSON.stringify({type}), token)),
        addRow: (after) =>
            change(() => postWithToken(`${at}/lines`, JSON.stringify({after: after === null ? null : id(after, 0)}), token)),
        deleteRow: (i) =>
            change(() => deleteWithToken(`${at}/lines/${i}`, token)),
        moveRow: (from, to) => {
            // row `from` ends at `to` - it goes under the row that will be above it
            const above = to === 0 ? null : (from < to ? to : to - 1);
            const after = above === null ? null : id(above, 0);
            const cell = id(from, 0);
            change(() => putWithToken(`${at}/lines/${cell}/move`, JSON.stringify({after}), token));
        },
        flagRow: (i) =>
            change(() => putWithToken(`${at}/lines/${id(i, 0)}/developer`, undefined, token)),
        addCol: (after) =>
            change(() => postWithToken(`${at}/columns`, JSON.stringify({after: after === null ? null : id(0, after)}), token)),
        deleteCol: (j) =>
            change(() => deleteWithToken(`${at}/columns/${j}`, token)),
        paste: (i, j, rows) =>
            change(() => postWithToken(`${at}/paste`, JSON.stringify({at: id(i, j), rows}), token)),
    };

    const exportToExel = () => {
        return data
    }

    return (
        <>
            <CardHeader>
                <CardTitle className="flex justify-between columns-2">
                    <div>Bid Setup</div>
                </CardTitle>
                <CardDescription>
                    Configure the Schedule of Values bidders will need to fill in. Changes are saved as you make them -
                    paste a block from Excel into a cell to fill in from there.
                </CardDescription>
                <Separator/>

            </CardHeader>
            {loaded && !config &&
                <div className="flex items-center gap-3 p-4 text-sm text-gray-600">
                    There is no Schedule of Values yet.
                    <Button variant="outline" onClick={onCreate}>Start one</Button>
                </div>}
            {config &&
                <div className="columns-2 w-full flex">
                    <div className="w-10/12">
                        {/* its own row above the grid - it used to float over the toolbar's row */}
                        <div className="flex gap-2 mb-2">
                            <CopyButton2 dataTransform={exportToExel} executeCustomCopy={copySheetDataToClipboard}></CopyButton2>
                        </div>
                        <DataSheet
                            data={data}
                            setData={setData}
                            enable_row_col={true}
                            readonly={false}
                            rowMeta={rowMeta}
                            edits={edits}
                        />
                    </div>
                </div>}
        </>
    );
}
